// WP-0.7: auth session state via TanStack Query over /api/v1/auth/status (S-01/02 routing).
import { authStatusResponseSchema, type AuthStatusResponse } from '@pfos/shared';
import { useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from './api';

export const AUTH_STATUS_KEY = ['auth', 'status'] as const;

export function useAuthStatus(): UseQueryResult<AuthStatusResponse> {
  return useQuery({
    queryKey: AUTH_STATUS_KEY,
    queryFn: async () => authStatusResponseSchema.parse(await api.get('/api/v1/auth/status')),
    staleTime: 5_000,
  });
}

/**
 * Mark the session authenticated in the cache SYNCHRONOUSLY after a successful login,
 * then refetch to confirm. Without the synchronous write, navigating to a gated route
 * re-reads the stale {authenticated:false} status and bounces the user back to /login.
 * `onboarded` is the optimistic value for the onboarding gate — false after first-run setup
 * (heading straight into S-04), true on a returning login; the refetch corrects any exception.
 */
export function markAuthenticated(queryClient: QueryClient, opts: { onboarded: boolean }): void {
  queryClient.setQueryData<AuthStatusResponse>(AUTH_STATUS_KEY, {
    ownerExists: true,
    authenticated: true,
    onboarded: opts.onboarded,
  });
  void queryClient.invalidateQueries({ queryKey: AUTH_STATUS_KEY });
}
